import clsx from "clsx";
import { MapPin, Network, Swords, UserRound, Users } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

import type { CompositionSide, CompositionUnit, DeployedUnit, OrbatNode, Phase, Side } from "../api/types";
import { Callout } from "../components/Callout";
import { EmptyState, Spinner } from "../components/EmptyState";
import { Disclosure, PanelGroup, TreeRow } from "../components/panel";
import { Badge, FloatingPanel, type IconProp, renderIcon, SearchField, Segmented } from "../components/ui";
import { HelpButton } from "../help/HelpButton";
import { symbolSvg } from "../map/units/symbols";
import { useDeployment } from "../map/units/useDeployment";
import { useOrbat } from "../map/units/useOrbat";
import { useUnitMoves } from "../map/units/useUnitMoves";
import { echelonName, echelonNameFromSidc } from "../echelon";
import { isNeutral } from "../enemyDisplay";
import { unitDesignation } from "../i18n/designation";
import { k, t } from "../i18n/i18n";
import { specialtyLabel } from "../missions";
import { type ObjectsFilter, useUi } from "../state/ui";
import { AddUnitButton, DossierUnits, EniGlobalLine, RemovedUnits, UnitBadges } from "./composition/DossierUnits";
import { UnitRowMenu } from "./composition/RoleMenu";
import { type SideView, sideView, withoutRemoved, withoutRemovedAll } from "./composition/rows";
import { useUnitActions } from "./composition/useUnitActions";
import { useLocate } from "./MissionObjects";
import { OpenMissionDataButton } from "./openMissionData";
import { LIST_DRAG_ZONE, useListDrag } from "./useListDrag";
import { refuseIfDossierLocked } from "../sharing/access";
import { Waiting } from "../components/Waiting";

/** Ordre de bataille du dossier (« Objets de la mission », comme dans Prevail) : composition des forces amies
 *  (force commandée, puis ses subordonnés jusqu'aux groupes) et ennemies (unités déployées dans leur
 *  filiation). Les unités présentes sur la carte au temps affiché sont signalées ; un clic les sélectionne et
 *  centre la carte dessus. Elles se glissent aussi de la liste vers la carte (docs/14 §2.6) : déplacées au temps
 *  affiché, comme par un glissé sur la carte — à la souris par toute la ligne, au doigt par l'épingle. */

const FILTERS: { value: Extract<ObjectsFilter, "all" | "AMI" | "ENI">; label: string }[] = [
  { value: "all", label: k("Tous") },
  { value: "AMI", label: k("Amis") },
  { value: "ENI", label: k("Ennemis") },
];

/** Unités dessinées sur la carte au temps affiché, par code (celles d'un camp donné). */
export function unitsOnMap(
  units: DeployedUnit[] | undefined,
  phase: Phase,
  side?: Side,
): DeployedUnit[] {
  return (units ?? []).filter(
    (u) => (!side || u.side === side) && u.positions[phase] && !u.hidden,
  );
}

function fold(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/** Arbre réduit aux unités dont le nom ou le code contient la recherche, avec leurs formations supérieures. */
function filterTree(node: OrbatNode, query: string): OrbatNode | null {
  const children = node.children
    .map((c) => filterTree(c, query))
    .filter((c): c is OrbatNode => c !== null);
  if (children.length || fold(`${node.label} ${unitDesignation(node.label)} ${node.code}`).includes(query))
    return { ...node, children };
  return null;
}

/** Codes des unités à montrer d'emblée : formations supérieures des unités présentes sur la carte. */
function openPaths(
  node: OrbatNode,
  onMap: Set<string>,
  out = new Set<string>(),
): boolean {
  let below = false;
  for (const child of node.children)
    below = openPaths(child, onMap, out) || below;
  if (below) out.add(node.code);
  return below || onMap.has(node.code);
}

function materielText(node: OrbatNode): string {
  if (!node.materiel.length) return node.quantity ? String(node.quantity) : "";
  return node.quantity
    ? `${node.quantity} × ${node.materiel.join(", ")}`
    : node.materiel.join(", ");
}

interface TreeContext {
  phase: Phase;
  units: Map<string, DeployedUnit>;
  /** Unités du dispositif (tous temps), pour distinguer « absente à ce temps » de « non placée ». */
  deployed: Set<string>;
  open: Set<string>;
  searching: boolean;
  friendly: boolean;
  /** Dispositif face à l'ennemi générique (force non française, registre B39) : aucune « référence » nommée. */
  neutral: boolean;
  drag: ListDrag;
  /** Composition du dossier du camp (docs/18 §6.1) : menu de ligne (rôle, retrait) et pastilles ; null sans elle. */
  composition: CompositionContext | null;
}

interface CompositionContext {
  view: SideView;
  menu: string | null;
  setMenu: (id: string | null) => void;
  actions: ReturnType<typeof useUnitActions>;
}

/** Unité de la composition qui correspond à une ligne de l'arbre (clé des déplacements, sinon code). */
function memberOf(view: SideView, node: OrbatNode): CompositionUnit | undefined {
  const key = node.key ?? node.code;
  return view.out.units.find((u) => u.depth > 0 && (u.key === key || u.code === node.code || u.id === key));
}

type DragUnit = { id: string; label: string; sidc: string };
type ListDrag = Pick<ReturnType<typeof useListDrag<DragUnit>>, "bind" | "dragging">;

function OrbatRow({
  node,
  depth,
  ctx,
}: {
  node: OrbatNode;
  depth: number;
  ctx: TreeContext;
}) {
  const [openState, setOpen] = useState<boolean | null>(null);
  const select = useUi((s) => s.select);
  const selection = useUi((s) => s.selection);
  const flyTo = useUi((s) => s.requestFlyTo);
  const hasChildren = node.children.length > 0;
  const open =
    ctx.searching || (openState ?? (depth === 0 || ctx.open.has(node.code)));
  const unit = ctx.units.get(node.code);
  const position = unit?.positions[ctx.phase];
  const selected = selection?.layer === "unit" && selection.code === node.code;
  const effort = unit?.effort.includes(ctx.phase) ?? false;
  const materiel = materielText(node);
  const member = depth > 0 && ctx.composition ? memberOf(ctx.composition.view, node) : undefined;
  // Ennemi de référence : formation du catalogue qui encadre les unités du dossier sans en être une (D4) — ni rôle ni
  // retrait ; la pastille dit pourquoi et ce qu'il faut faire (visible au doigt, pas seulement en bulle).
  const referenceOnly =
    depth > 0 && !member && !ctx.friendly && Boolean(ctx.composition?.view.out.reference) && !ctx.composition?.view.legacy;
  const absence = unit
    ? undefined
    : ctx.deployed.has(node.code)
      ? t("Absente de la carte en {phase}", { phase: ctx.phase })
      : ctx.friendly && depth === 1
        ? t("Non placée sur la carte")
        : undefined;
  const details = [
    specialtyLabel(node.specialty),
    // Unité construite : échelon APP-6 choisi dans l'éditeur (symbole) ; catalogue : échelon moteur.
    (node.app6 ? echelonNameFromSidc(node.sidc, node.arm, ctx.friendly) : echelonName(node.echelon, node.arm, ctx.friendly))?.toLowerCase(),
  ]
    .filter(Boolean)
    .join(" · ");

  // Branche montée à sa première ouverture (un ODB de division compte des centaines d'unités), puis gardée pour
  // que son repli s'anime.
  const [mounted, setMounted] = useState(open);
  if (open && hasChildren && !mounted) setMounted(true);
  const locate =
    unit && position
      ? () => {
          select({ layer: "unit", code: unit.code, lngLat: [position.lon, position.lat] });
          flyTo([position.lon, position.lat], 13);
        }
      : hasChildren && !ctx.searching
        ? () => setOpen(!open)
        : undefined;

  // Unité sur la carte à ce temps : elle se glisse vers un autre point (déplacée au temps affiché).
  const handlers = unit && position ? ctx.drag.bind({ id: unit.key, label: node.label, sidc: unit.sidc }) : undefined;
  return (
    <li>
      <div
        {...handlers}
        className={clsx(
          handlers && "cursor-grab select-none",
          unit && ctx.drag.dragging === unit.key && "opacity-40",
          "transition-opacity duration-(--gx-enter)",
        )}
      >
        <TreeRow
          depth={depth}
          selected={selected}
          expanded={open}
          onToggle={hasChildren && !ctx.searching ? () => setOpen(!open) : undefined}
          toggleLabel={open ? t("Replier") : t("Déplier ({n})", { n: node.children.length })}
          onClick={locate}
          title={[details, unit ? t("Sur la carte : cliquer pour la localiser, glisser pour la déplacer") : absence].filter(Boolean).join("\n")}
          icon={
            <span
              className={clsx(
                "flex h-4 w-6 shrink-0 items-center justify-center [&>svg]:max-h-4 [&>svg]:max-w-6",
                absence && "opacity-40",
              )}
              dangerouslySetInnerHTML={{ __html: node.sidc ? symbolSvg(node.sidc, 13) : "" }}
            />
          }
          label={<span className={clsx(absence ? "text-ink-muted" : "text-ink", unit && "font-medium")}>{unitDesignation(node.label)}</span>}
          trailing={
            <>
              {member && <UnitBadges unit={member} compact />}
              {referenceOnly &&
                (ctx.neutral ? (
                  <Badge
                    size="sm"
                    title={t("Formation de l'ennemi générique, hors du dossier : elle n'a ni rôle ni retrait. Pour changer ce que cite l'OPO, ajoutez une unité au même rôle.")}
                  >
                    {t("Générique")}
                  </Badge>
                ) : (
                  <Badge
                    size="sm"
                    title={t("Formation de référence, hors du dossier : elle n'a ni rôle ni retrait. Pour changer ce que cite l'OPO, ajoutez une unité au même rôle.")}
                  >
                    {t("Référence")}
                  </Badge>
                ))}
              {effort && (
                <Badge dot size="sm" tone="accent">
                  {t("Effort")}
                </Badge>
              )}
              {materiel && (
                <span className="max-w-[112px] shrink-0 truncate text-meta text-ink-muted tabular-nums" title={materiel}>
                  {materiel}
                </span>
              )}
              {member && ctx.composition && (
                <CompositionMenu composition={ctx.composition} unit={member} />
              )}
              {unit ? (
                <span
                  data-drag-handle=""
                  data-no-press=""
                  className="flex size-6 shrink-0 touch-none items-center justify-center rounded-md text-ink-muted"
                  title={position ? t("Glisser sur la carte") : undefined}
                >
                  <MapPin size={12} aria-label={t("Sur la carte")} />
                </span>
              ) : (
                <span aria-hidden className="size-6 shrink-0" />
              )}
            </>
          }
        />
      </div>
      {hasChildren && (
        <Disclosure open={open}>
          {mounted && (
            <ul>
              {node.children.map((child) => (
                <OrbatRow key={child.code} node={child} depth={depth + 1} ctx={ctx} />
              ))}
            </ul>
          )}
        </Disclosure>
      )}
    </li>
  );
}

/** Menu de ligne d'une unité de la composition (rôle dans ce dossier, retrait du dossier) dans l'arbre. */
function CompositionMenu({ composition, unit }: { composition: CompositionContext; unit: CompositionUnit }) {
  const { view, menu, setMenu, actions } = composition;
  const { writes } = actions;
  const side: CompositionSide = unit.side;
  return (
    <UnitRowMenu
      view={view}
      unit={unit}
      open={menu === unit.id}
      onOpenChange={(open) => setMenu(open ? unit.id : null)}
      busy={writes.busy}
      draggable={false}
      onRole={(role) => actions.guard(() => writes.setRole.mutate({ side, unit: unit.id, role }))}
      onUnplace={() => actions.guard(() => writes.unplace.mutate(unit.key))}
      onRemove={() => actions.guard(() => writes.compose.mutate({ op: "remove", side, key: unit.id }))}
      onCommand={(command) => actions.guard(() => writes.compose.mutate({ op: "command", side: "AMI", key: unit.id, command }))}
    />
  );
}

/** Groupe d'un camp (Amis, Ennemis, Populations) : titre gris, compteur des unités sur la carte (le détail est dans
 *  la bulle du compteur). */
function SideGroup({
  icon,
  title,
  count,
  extra,
  children,
}: {
  icon: IconProp;
  title: string;
  count: number;
  /** Action d'en-tête avant le compteur (« Ajouter une unité… »). */
  extra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <PanelGroup
      title={title}
      leading={<span className="flex shrink-0 text-ink-muted">{renderIcon(icon, 14)}</span>}
      actions={
        <>
          {extra}
          <Badge title={t("{n} sur la carte", { n: count })}>{count}</Badge>
        </>
      }
    >
      {children}
    </PanelGroup>
  );
}

export function OrbatPanel() {
  const filter = useUi((s) => s.objectsFilter);
  const showObjects = useUi((s) => s.showObjects);
  const phase = useUi((s) => s.phase);
  const params = useUi((s) => s.deploymentParams);
  const { data: deployment } = useDeployment();
  const { data: orbat, error, isLoading } = useOrbat();
  const [query, setQuery] = useState("");
  const q = fold(query.trim());

  const view = useMemo(() => {
    const all = deployment?.units ?? [];
    const shown = unitsOnMap(all, phase);
    const units = new Map(shown.map((u) => [u.code, u]));
    const deployed = new Set(all.map((u) => u.code));
    const onMap = new Set(units.keys());
    const open = new Set<string>();
    // Unités retirées du dossier : seulement dans le volet « Retirées du dossier », jamais aussi dans l'arbre.
    const amiTree = orbat ? withoutRemoved(orbat.ami, sideView(deployment, "AMI")) : null;
    const eniTree = withoutRemovedAll(orbat?.eni ?? [], sideView(deployment, "ENI"));
    const ami = amiTree ? (q ? filterTree(amiTree, q) : amiTree) : null;
    const eni = eniTree.map((n) => (q ? filterTree(n, q) : n)).filter((n): n is OrbatNode => n !== null);
    for (const root of eniTree) openPaths(root, onMap, open);
    const neutral = shown.filter(
      (u) =>
        u.side === "NEUTRE" &&
        (!q || fold(`${u.designation} ${unitDesignation(u.designation)} ${u.code}`).includes(q)),
    );
    const count = (side: Side) => shown.filter((u) => u.side === side).length;
    return {
      ami,
      eni,
      neutral,
      units,
      deployed,
      open,
      counts: { AMI: count("AMI"), ENI: count("ENI"), NEUTRE: count("NEUTRE") },
    };
  }, [deployment, orbat, phase, q]);

  const total = view.counts.AMI + view.counts.ENI + view.counts.NEUTRE;
  const select = useUi((s) => s.select);
  const notify = useUi((s) => s.notify);
  const requestSave = useUi((s) => s.requestSave);
  const exerciseId = useUi((s) => s.exerciseId);
  const moves = useUnitMoves();
  // Glisser une unité de la liste vers la carte : même écriture qu'un glissé sur la carte (`PUT /positions`).
  const drag = useListDrag<DragUnit>({
    canEdit: moves.canMove,
    refuseNotSaved: () => {
      if (refuseIfDossierLocked()) return; // lecture seule (docs/17 §3.2) : le bandeau, pas l'enregistrement
      const action = t("déplacer les unités");
      requestSave();
      notify({
        text: exerciseId
          ? t("Enregistrez les modifications du dossier pour {action}", { action })
          : t("Créez le dossier pour {action}", { action }),
        detail: t("Les déplacements sont mémorisés dans le dossier d'exercice."),
        tone: "info",
      });
    },
    drop: (item, at) => {
      const unit = deployment?.units.find((u) => u.key === item.id);
      if (unit) select({ layer: "unit", code: unit.code, lngLat: at });
      moves.move.mutate({ unit: item.id, phase, lon: at[0], lat: at[1] });
    },
  });
  // Composition du dossier (docs/18 §6.1) : un menu de ligne ouvert à la fois, toutes listes confondues.
  const actions = useUnitActions();
  const [menu, setMenu] = useState<string | null>(null);
  const amiView = sideView(deployment, "AMI");
  const eniView = sideView(deployment, "ENI");
  const compositionOf = (v: SideView | null): CompositionContext | null => (v ? { view: v, menu, setMenu, actions } : null);
  const ctx = (friendly: boolean): TreeContext => ({
    phase,
    units: view.units,
    deployed: view.deployed,
    open: view.open,
    searching: Boolean(q),
    friendly,
    neutral: isNeutral(deployment?.reference_enemy),
    drag,
    composition: compositionOf(friendly ? amiView : eniView),
  });
  const selection = useUi((s) => s.selection);
  const flyTo = useUi((s) => s.requestFlyTo);
  const locate = useLocate();

  let body: ReactNode;
  if (!params) {
    body = (
      <EmptyState variant="inline" action={<OpenMissionDataButton />}>
        {t("Choisissez un camp et une mission dans les Données initiales pour composer l'ordre de bataille.")}
      </EmptyState>
    );
  } else if (error) {
    body = (
      <div className="px-4 py-4">
        <Callout tone="danger">{error instanceof Error ? error.message : t("Ordre de bataille indisponible.")}</Callout>
      </div>
    );
  } else if (isLoading || !orbat) {
    body = (
      <div className="flex items-center gap-2 px-4 py-4 text-ui text-ink-muted">
        <Spinner decorative /> {<Waiting>{t("Composition des forces…")}</Waiting>}
      </div>
    );
  } else {
    const nothing = q && !view.ami && !view.eni.length && !view.neutral.length;
    body = (
      <div {...LIST_DRAG_ZONE}>
        {nothing && (
          <p className="px-4 py-3 text-ui text-ink-muted">
            {t("Aucune unité ne correspond à « {query} ».", { query: query.trim() })}
          </p>
        )}
        {filter !== "ENI" && view.ami && (
          <SideGroup icon={Users} title={t("Amis")} count={view.counts.AMI} extra={<AddUnitButton side="AMI" compact />}>
            {/* Unités amies à glisser sur la carte (ajoutées, en surnombre, docs/18 §6.1). */}
            {!q && <DossierUnits side="AMI" onLocate={locate} placeableOnly />}
            <ul>
              <OrbatRow node={view.ami} depth={0} ctx={ctx(true)} />
            </ul>
            {!q && <RemovedUnits side="AMI" />}
          </SideGroup>
        )}
        {filter !== "AMI" && (view.eni.length > 0 || Boolean(deployment?.eni_odb) || Boolean(deployment?.composition)) && (
          <SideGroup icon={Swords} title={t("Ennemis")} count={view.counts.ENI} extra={<AddUnitButton side="ENI" compact />}>
            {/* Unités ennemies du dossier à glisser sur la carte (docs/14 §2.6, docs/18 §6.1). */}
            {!q && <DossierUnits side="ENI" onLocate={locate} placeableOnly />}
            <ul>
              {view.eni.map((root) => (
                <OrbatRow
                  key={root.code}
                  node={root}
                  depth={0}
                  ctx={ctx(false)}
                />
              ))}
            </ul>
            {!q && <EniGlobalLine />}
            {!q && <RemovedUnits side="ENI" />}
          </SideGroup>
        )}
        {filter === "all" && view.neutral.length > 0 && (
          <SideGroup icon={UserRound} title={t("Populations")} count={view.counts.NEUTRE}>
            <ul>
              {view.neutral.map((u) => {
                const position = u.positions[phase]!;
                const selected = selection?.layer === "unit" && selection.code === u.code;
                return (
                  <li key={u.code}>
                    <TreeRow
                      selected={selected}
                      onClick={() => {
                        select({ layer: "unit", code: u.code, lngLat: [position.lon, position.lat] });
                        flyTo([position.lon, position.lat], 13);
                      }}
                      icon={
                        <span
                          className="flex h-4 w-6 shrink-0 items-center justify-center [&>svg]:max-h-4 [&>svg]:max-w-6"
                          dangerouslySetInnerHTML={{ __html: symbolSvg(u.sidc, 13) }}
                        />
                      }
                      label={unitDesignation(u.designation)}
                      trailing={
                        <span className="flex w-3.5 shrink-0 justify-center">
                          <MapPin size={12} className="text-ink-muted" aria-label={t("Sur la carte")} />
                        </span>
                      }
                    />
                  </li>
                );
              })}
            </ul>
          </SideGroup>
        )}
      </div>
    );
  }

  return (
    <FloatingPanel
      enter="left"
      width="view"
      icon={Network}
      title={t("Ordre de bataille")}
      actions={<HelpButton screen="carte" topic="carte.odb" buttonSize="sm" size={16} />}
    >
      <div className="sticky top-0 z-10 space-y-2 border-b border-line bg-surface px-4 py-2.5">
        <Segmented
          ariaLabel={t("Objets montrés")}
          value={filter === "AMI" || filter === "ENI" ? filter : "all"}
          onChange={(value) => showObjects(value)}
          options={FILTERS.map((f) => ({
            value: f.value,
            label: t(f.label),
            count: deployment ? (f.value === "all" ? total : view.counts[f.value]) : undefined,
          }))}
        />
        <SearchField value={query} onChange={setQuery} placeholder={t("Rechercher une unité")} />
      </div>
      {body}
      {drag.layer}
    </FloatingPanel>
  );
}
